/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { InfoBanner } from '../../InfoBanner/InfoBanner';
import { useContext, type ReactNode } from 'react';
import { CustomerPortalContext } from '../../../contexts/CustomerPortalContext';
import type { CodeOutput, InstallMethod } from '../../../constants/installation';
import { BannerButton } from '../BannerButton/BannerButton';

type OutputBannerProps = {
	arePremiumFeaturesSelected: boolean;
	allFeaturesAvailableInPlan: boolean;
	installMethod?: InstallMethod;
	codeOutput?: CodeOutput;
};

export function OutputBanner( {
	arePremiumFeaturesSelected,
	allFeaturesAvailableInPlan,
	installMethod,
	codeOutput
}: OutputBannerProps ): ReactNode {
	const { isLoggedIn, isTrialActive, hasSelfHostedAccess, hasOldGPLAccess } = useContext( CustomerPortalContext );

	if ( isLoggedIn && codeOutput === 'stackblitz' ) {
		return null;
	}

	if ( isTrialActive || hasSelfHostedAccess ) {
		return (
			<InfoBanner data-testid={ 'output-banner-trial-or-self-hosted' } variant={ 'success' }>
				All required fields have been filled with your license keys, so you can start with your { ' ' }
				{ installMethod?.toUpperCase() } integration right away.
			</InfoBanner>
		);
	}

	if ( !isLoggedIn && !arePremiumFeaturesSelected ) {
		if ( installMethod !== 'cdn' ) {
			return null;
		}

		return (
			<InfoBanner data-testid={ 'output-banner-not-logged-in-no-premium-selected-cdn' } variant={ 'warning' }>
				An evaluation key will be used if you set up the project without logging in. { ' ' }
				Create an account to use your own license keys.
			</InfoBanner>
		);
	}

	if ( !isLoggedIn && arePremiumFeaturesSelected ) {
		return (
			<InfoBanner data-testid={ 'output-banner-not-logged-in-premium-selected' } variant={ 'warning' }>
				To use selected features, you will need a <b>license key.</b> { ' ' }
				Create an account in our Customer Portal to access it, and we'll automatically fill in the required fields.
			</InfoBanner>
		);
	}

	if ( !hasOldGPLAccess && installMethod !== 'cdn' ) {
		return (
			<InfoBanner
				data-testid={ 'output-banner-no-self-hosted-no-cdn-no-old-gpl' } variant={ 'error' } buttonRight={
					<BannerButton href={ 'https://ckeditor.com/contact/?sales=true#contact-form' }>
						Contact sales
					</BannerButton>
				}
			>
				This option isn't available for your account, which only supports cloud (CDN). Interested in self-hosting? { ' ' }
				Contact our sales team.
			</InfoBanner>
		);
	}

	if ( hasOldGPLAccess && !allFeaturesAvailableInPlan ) {
		return (
			<InfoBanner data-testid={ 'output-banner-not-all-features-in-plan-old-gpl' } variant={ 'warning' }>
				Your plan doesn't include all the features you've selected. { ' ' }
				The editor won't function correctly unless you remove those features or upgrade your plan.
			</InfoBanner>
		);
	}

	if ( !allFeaturesAvailableInPlan ) {
		return (
			<InfoBanner data-testid={ 'output-banner-not-all-features-in-plan' } variant={ 'warning' }>
				The license key fields have been automatically filled, { ' ' }
				but your plan doesn't include all the features you've selected. { ' ' }
				The editor won't function correctly unless you remove those features or upgrade your plan.
			</InfoBanner>
		);
	}

	if ( installMethod === 'cdn' ) {
		return (
			<InfoBanner data-testid={ 'output-banner-cdn' } variant={ 'success' }>
				All required fields have been filled with your license keys, so you can start your CDN integration right away.
			</InfoBanner>
		);
	}
}
